import { formatMoney, formatDate } from '../lib/format';
import {
  useTransactionList,
  TransactionListToolbar,
  StatusBadge,
  Pager,
  ListStates,
  SortableTh,
  type StatusOption,
} from './TransactionList';
import {
  CustomerFilter,
  DateRangeFilter,
} from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripeChargeUrl, hubspotContactUrl } from './links';
import { ExtLink } from './ExtLink';
import type { ChargeListEntry } from './types';

/**
 * Dashboard → Charges (M19 T2 + T3).
 *
 * The money-in ledger: one row per Stripe Charge. Filterable by status (the
 * single highest-value filter — "failed charges" is the dunning/ops queue),
 * the universal Customer typeahead, and a date-range preset. Customer name is
 * enriched from customer_mappings.
 *
 * Reference: docs/features/ (M12 money-flow). Mirrors the invoices view.
 */

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'succeeded', label: 'Succeeded', tone: 'success' },
  { value: 'pending', label: 'Pending', tone: 'brand' },
  { value: 'failed', label: 'Failed', tone: 'danger' },
];

export function Charges() {
  const list = useTransactionList<ChargeListEntry>('admin/charges', 'status');
  const {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Charges"
        subtitle="Stripe Charges — captured money and payment failures."
      />
      <TransactionListToolbar
        status={status}
        setStatus={setStatus}
        statusOptions={STATUS_OPTIONS}
        statusLabel="Status"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Charge</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Created"
                    sortKey="created"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((c) => (
                  <ChargeRow key={c.id} charge={c} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function ChargeRow({ charge }: { charge: ChargeListEntry; }) {
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeChargeUrl(stripeAccountId, charge.id);
  const hsUrl = hubspotContactUrl(portalId, charge.customerEmail ?? '');
  const isDisputed = charge.disputeId != null;
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={charge.id} title="View in Stripe" />
        ) : (
          <span className="text-xs text-ink-600">{charge.id}</span>
        )}
        {isDisputed && (
          <span className="ml-2 rounded-full bg-danger-500/10 px-1.5 py-0.5 text-[10px] font-medium text-danger-600">
            ⚠ disputed
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col">
          <span className="text-ink-800">{charge.customerName ?? '—'}</span>
          {charge.customerEmail && (
            <span className="text-xs text-ink-400">
              {hsUrl ? (
                <ExtLink href={hsUrl} label={charge.customerEmail} title="View in HubSpot" />
              ) : (
                charge.customerEmail
              )}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-right text-ink-800">
        {formatMoney(charge.amountMinor, charge.currency)}
        {charge.amountRefundedMinor > 0 && (
          <span className="ml-1 text-xs text-ink-400">
            (−{formatMoney(charge.amountRefundedMinor, charge.currency)} ref.)
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={charge.status} options={STATUS_OPTIONS} />
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500" title={formatDate(charge.createdAt)}>
        {formatDate(charge.createdAt)}
      </td>
    </tr>
  );
}
